import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { LocalizationService } from '@abp/ng.core';
import { ConfirmationService, Toaster, ToasterService } from '@abp/ng.theme.shared';

import { deepCopy, isNullOrUndefined } from '@nexweb/util';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private toasterService = inject(ToasterService);
  private localizationService = inject(LocalizationService);
  private confirmationService = inject(ConfirmationService);

  private _options = {
    life: 10000,
    closable: true,
    sticky: false,
  } as Toaster.ToastOptions;

  success(
    message: string,
    sticky?: boolean,
    closable?: boolean,
    options?: NotificationDisplayOptions,
  ): Toaster.ToasterId {
    return this._display('success', message, sticky, closable, options);
  }

  info(message: string, sticky?: boolean, closable?: boolean, options?: NotificationDisplayOptions): Toaster.ToasterId {
    return this._display('info', message, sticky, closable, options);
  }

  error(
    message: string,
    sticky?: boolean,
    closable?: boolean,
    options?: NotificationDisplayOptions,
  ): Toaster.ToasterId {
    if (sticky === undefined) sticky = true;

    return this._display('error', message, sticky, closable, options);
  }

  warn(message: string, sticky?: boolean, closable?: boolean, options?: NotificationDisplayOptions): Toaster.ToasterId {
    if (sticky === undefined) sticky = false;

    return this._display('warn', message, sticky, closable, options);
  }

  /**
   * Remove a single, specific toast by the id returned from info()/success()/warn()/error() —
   * unlike clear(), this leaves every other visible toast untouched.
   */
  public remove(id: Toaster.ToasterId): void {
    this.toasterService.remove(id as number);
  }

  public saved(messageKey?: string): void {
    if (messageKey === undefined) {
      messageKey = 'NexBase::Message:Saved';
    }
    this.success(messageKey);
  }

  public clear() {
    this.toasterService.clear();
  }

  public noChanges(messageKey?: string): void {
    if (messageKey === undefined) {
      messageKey = 'NexBase::Message:NoChanges';
    }
    this.info(messageKey);
  }

  //Clear Confirm Message
  public clearConfirmMessage(): void {
    if (typeof this.confirmationService.clear === 'function' && !isNullOrUndefined(this.confirmationService.status$)) {
      this.confirmationService.clear();
    }
  }

  /**
   * Display error as code
   */
  public errorCode(
    response: HttpErrorResponse,
    moduleName: string,
    fallbackMessage: string,
    clearConfirmMessage: boolean = true,
  ): void {
    if (clearConfirmMessage) {
      this.clearConfirmMessage();
    }

    let message = this.localizationService.instant(fallbackMessage);

    if (response.error && !isNullOrUndefined(response.error.error?.code)) {
      message = this.localizationService.instant(`${moduleName}::${response.error.error.code}`);
    } else {
      if (!isNullOrUndefined(response.error.error?.message)) {
        message = response.error.error?.message;
      }
    }

    this.error(message);
  }

  /**
   * @param type severity bucket
   * @param message localization key, or already-resolved text when options.literal is set
   * @param sticky
   * @param closable
   * @param options optional title and per-toast overrides (SignalR-driven toasts). This is the single
   *   place that talks to the ABP toaster — callers stay unaware of the underlying notification mechanism.
   */
  private _display(
    type: 'info' | 'success' | 'warn' | 'error',
    message: string,
    sticky?: boolean,
    closable?: boolean,
    options?: NotificationDisplayOptions,
  ): Toaster.ToasterId {
    const _options = deepCopy(this._options);

    if (sticky != null) _options.sticky = sticky;
    if (closable != null) _options.closable = closable;
    if (options?.life != null) _options.life = options.life;

    const localisedMessage = options?.literal
      ? message
      : this.localizationService.instant(message, ...(options?.messageArgs ?? []));

    const title =
      options?.title ?? (options?.titleKey ? this.localizationService.instant(options.titleKey) : undefined);

    switch (type) {
      case 'error':
        return this.toasterService.error(localisedMessage, title, _options);

      case 'info':
        return this.toasterService.info(localisedMessage, title, _options);

      case 'success':
        return this.toasterService.success(localisedMessage, title, _options);

      case 'warn':
        return this.toasterService.warn(localisedMessage, title, _options);

      default: {
        const exhaustiveCheck: never = type;
        throw new Error(`Unhandled notification type: ${exhaustiveCheck}`);
      }
    }
  }
}

/**
 * Framework-agnostic options for a notification. Deliberately free of any ABP toaster type so callers
 * (e.g. the SignalR hub) depend only on this contract, not on the underlying notification mechanism.
 */
export interface NotificationDisplayOptions {
  /** Already-resolved title text. Takes precedence over titleKey. */
  title?: string;
  /** Localization key for the title, resolved by the service. */
  titleKey?: string;
  /** Positional args applied to the localized message ({0}, {1}, ...). Ignored when literal is set. */
  messageArgs?: string[];
  /** Treat the message argument as already-resolved literal text (skip localization). */
  literal?: boolean;
  /** Auto-dismiss time in ms. */
  life?: number;
}
